import { Drawer, FocusTrap, Loader } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import ContentLoader from 'react-content-loader';
import { FilterButton } from 'components/OrdersFilters/FilterButton';
import { EmployedsMobileFilterCheckbox } from './EmployedsMobileFilterCheckbox';
import { ReactComponent as IconClose } from 'assets/img/close.svg';
import { ReactComponent as IconSearch } from './img/iconSearch.svg';
import styles from './EmployedsMobileFilterDrawer.module.scss';

export interface FilterOption {
  id: string;
  label: string;
}

interface EmployedsMobileFilterDrawerProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  onApply: () => void;
  onReset: () => void;
  showSearch?: boolean;
  categoriesServer?: boolean;
  onCategoriesSearch?: (search: string) => void;
  categoriesHasNext?: boolean;
  onCategoriesLoadMore?: () => void;
  categoriesFetching?: boolean;
  categoriesLoading?: boolean;
}

export const EmployedsMobileFilterDrawer: React.FC<EmployedsMobileFilterDrawerProps> = ({
  opened,
  onClose,
  title,
  options,
  selectedValues,
  onChange,
  onApply,
  onReset,
  showSearch = false,
  categoriesServer = false,
  onCategoriesSearch,
  categoriesHasNext = false,
  onCategoriesLoadMore,
  categoriesFetching = false,
  categoriesLoading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchQuery.trim(), 300);
  const loadMoreLockRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!categoriesServer || !opened) return;
    onCategoriesSearch?.(debouncedSearch);
  }, [debouncedSearch, categoriesServer, opened, onCategoriesSearch]);

  useEffect(() => {
    if (!categoriesFetching) loadMoreLockRef.current = false;
  }, [categoriesFetching]);

  const handleLoadMore = useCallback(() => {
    if (
      !categoriesServer ||
      !onCategoriesLoadMore ||
      !categoriesHasNext ||
      categoriesFetching ||
      loadMoreLockRef.current
    ) {
      return;
    }
    loadMoreLockRef.current = true;
    onCategoriesLoadMore();
  }, [categoriesServer, onCategoriesLoadMore, categoriesHasNext, categoriesFetching]);

  const handleScroll = useCallback(() => {
    if (!categoriesServer) return;
    const el = scrollRef.current;
    if (!el || el.scrollHeight <= el.clientHeight) return;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (gap < 72 && el.scrollTop > 12) handleLoadMore();
  }, [categoriesServer, handleLoadMore]);

  const filteredOptions = useMemo(() => {
    const valid = options.filter((o) => o.label != null && String(o.label).trim() !== '');
    if (categoriesServer) return valid;
    if (!showSearch || !searchQuery.trim()) return valid;
    return valid.filter((o) => o.label.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [options, searchQuery, showSearch, categoriesServer]);

  const handleOptionChange = (optionId: string, checked: boolean) => {
    if (checked) {
      onChange([...selectedValues, optionId]);
    } else {
      onChange(selectedValues.filter((id) => id !== optionId));
    }
  };

  const handleReset = () => {
    onChange([]);
    onReset();
  };

  const handleApply = () => {
    onApply();
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  useEffect(() => {
    if (!opened) setSearchQuery('');
  }, [opened]);

  const showSearchRow = showSearch || categoriesServer;
  const listEmpty = filteredOptions.length === 0;
  const showCategoriesSkeleton = categoriesServer && categoriesLoading && listEmpty;

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position='bottom'
      zIndex={10000}
      withCloseButton={false}
      styles={{
        content: {
          height: 'fit-content',
          maxHeight: '90vh',
          borderRadius: '13px 13px 0px 0px',
          backgroundColor: '#FFF',
          padding: '16px 16px 4px 16px',
          boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          overflow: 'hidden',
        },
      }}>
      <FocusTrap.InitialFocus />
      <div className={styles.header}>
        <span className={styles.headerTitle}>{title}</span>
        <span className={styles.headerClear} onClick={handleClose}>
          Готово
        </span>
      </div>

      {showSearchRow && (
        <div className={styles.searchWrapper}>
          <IconSearch className={styles.searchIcon} />
          <input
            type='text'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Искать...'
            className={styles.searchInput}
          />
          {searchQuery && (
            <IconClose className={styles.closeIcon} onClick={handleClearSearch} />
          )}
        </div>
      )}

      <div
        ref={categoriesServer ? scrollRef : undefined}
        className={styles.selectItems}
        onScroll={categoriesServer ? handleScroll : undefined}>
        {showCategoriesSkeleton ? (
          <ContentLoader
            speed={2}
            height={150}
            width='100%'
            backgroundColor='#FFFFFF'
            foregroundColor='#D8E4FB8C'>
            <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
          </ContentLoader>
        ) : listEmpty ? (
          <div className={styles.noResults}>
            <span>Ничего не найдено</span>
          </div>
        ) : (
          <>
            {filteredOptions.map((option) => {
              const isChecked = selectedValues.includes(option.id);
              return (
                <div
                  key={option.id}
                  className={styles.selectItem}
                  onClick={() => handleOptionChange(option.id, !isChecked)}>
                  <span className={styles.selectItemLabel}>{option.label}</span>
                  <div onClick={(e) => e.stopPropagation()}>
                    <EmployedsMobileFilterCheckbox
                      checked={isChecked}
                      onChange={(checked) => handleOptionChange(option.id, checked)}
                    />
                  </div>
                </div>
              );
            })}
            {categoriesServer && categoriesFetching && categoriesHasNext && (
              <div className={styles.loadMoreRow}>
                <Loader size='xs' color='#002CFB' />
              </div>
            )}
          </>
        )}
      </div>

      <div className={styles.actionsRow}>
        <FilterButton variant='reset' onClick={handleReset} disabled={false}>
          Сбросить
        </FilterButton>
        <FilterButton variant='apply' onClick={handleApply} disabled={false}>
          Сохранить
        </FilterButton>
      </div>
    </Drawer>
  );
};
